import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useConfirm } from "material-ui-confirm";
import { Formik } from "formik";
import { ConnectedFocusError } from "focus-formik-error";
import { useTranslation } from "react-i18next";
import moment from "moment";
import "moment-timezone";
import { postData } from "../../../../../components/utilities/UtilityFunctions";
import {
  BCPCircleCheck,
  BCPEllipsisVertical
} from "../../../../../components/icons/bcp/BCPIcons";
import { postNotification } from "../../../../../components/utilities/Notifications";
import { AuthContext } from "../../../../../components/user/BCPAuthProvider";
import ModalLoadingMessage from "../../../../../components/modals/ModalLoadingMessage";
import EventCardPreview from "../../../../../components/layout/eventcard/EventCardPreview";

export default function EventCreationCircuits({
  getInitialValues,
  getValidationSchema,
  submitEvent,
  setEventValues,
  eventValues,
  hasSubAccess,
  theme,
  readOnly,
  gameSystem,
  eventId,
  event,
  step,
  setStep,
  EventStepper,
  circuits,
  setCircuits,
  circuitsRequested,
  setCircuitsRequested,
  buildPreviewEvent
}) {
  const { t } = useTranslation("translation", {
    keyPrefix: "components.eventCreation"
  });
  const initialValues = getInitialValues();
  const authContext = useContext(AuthContext);
  const muiTheme = useTheme();
  const isMdUp = useMediaQuery(muiTheme.breakpoints.up("md"));

  const confirm = useConfirm();

  const [loading, setLoading] = useState(circuits ? false : true);
  const [message, setMessage] = useState(null);
  const [circuitMenu, setCircuitMenu] = useState(null);

  const updateCircuits = (token) => {
    let _circuits = [...circuits];
    for (let circuit of _circuits) {
      if (circuit.id === token.leagueId) {
        if (!circuit.tokens) {
          circuit.tokens = [token];
        } else {
          let found = false;
          for (let i = 0; i < circuit.tokens.length; i++) {
            if (circuit.tokens[i].id === token.id) {
              circuit.tokens[i] = token;
              found = true;
              break;
            }
          }
          if (!found) {
            circuit.tokens.push(token);
          }
        }
      }
    }
    setCircuits(_circuits);
  };

  useEffect(() => {
    if (loading && circuits && circuits.length > 0) {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [circuits]);

  return (
    <Grid item xs={12} sx={{ pt: 2 }}>
      {message && <ModalLoadingMessage message={message} open />}

      <Formik
        initialValues={initialValues}
        validationSchema={getValidationSchema()}
        onSubmit={submitEvent}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
          setFieldValue,
          setFieldTouched
        }) => (
          <form onSubmit={handleSubmit}>
            {/* Event Creation Progress Stepper */}
            <EventStepper
              step={step}
              setStep={setStep}
              event={event}
              circuits={circuits}
            />

            <ConnectedFocusError />

            <Grid
              container
              spacing={2}
              pt={1}
              onBlur={() => {
                setEventValues(
                  eventValues ? { ...eventValues, ...values } : { ...values }
                );
              }}
            >
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Divider
                  sx={{
                    mt: 1,
                    mb: 1
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h4" fontWeight="700">
                  {t("eventCircuits")}
                </Typography>

                <Typography variant="caption" gutterBottom>
                  {t("circuitsDescription")}
                </Typography>
              </Grid>

              {eventValues?.testEvent && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    {t("testEventCircuitRestriction")}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                {loading ? (
                  <LinearProgress />
                ) : !loading && circuits?.length > 0 ? (
                  circuits
                    .filter(
                      (circuit) =>
                        (!circuit.startDate ||
                          moment(eventValues?.eventEndDate).isAfter(
                            moment(circuit.startDate)
                          )) &&
                        (!circuit.endDate ||
                          moment(eventValues?.eventEndDate).isBefore(
                            moment(circuit.endDate)
                          ))
                    )
                    .map((circuit) => {
                      let activeCircuit =
                        circuit.tokens?.filter(
                          (token) =>
                            token.status?.toLowerCase() !== "rejected" &&
                            token.status?.toLowerCase() !== "used"
                        )?.length <= 0;

                      return (
                        <Grid
                          item
                          xs={12}
                          key={circuit.id}
                          sx={{
                            p: 1,
                            mb: 1,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            boxShadow: 1
                          }}
                        >
                          <Grid container>
                            <Grid
                              item
                              xs={12}
                              sx={{
                                display: "fkex",
                                alignItems: "start",
                                justifyContent: "space-between"
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: "700" }}
                                >
                                  {circuit.name}
                                </Typography>

                                {circuit?.startDate && (
                                  <Typography variant="body2">
                                    {t("starts")}:{" "}
                                    {moment(circuit.startDate).format("LL")}
                                  </Typography>
                                )}

                                {circuit?.endDate && (
                                  <Typography variant="body2">
                                    {t("ends")}:{" "}
                                    {moment(circuit.endDate).format("LL")}
                                  </Typography>
                                )}
                              </Box>

                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCircuitMenu({
                                    anchorEl: e.currentTarget,
                                    circuit: circuit
                                  });
                                }}
                              >
                                <BCPEllipsisVertical />
                              </IconButton>
                            </Grid>

                            <Grid item xs={12}>
                            {circuit.tokens?.length > 0 && (
                              <>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "text.secondary", mt: 1, display: "block" }}
                                >
                                  {t("tokenHistory")}
                                </Typography>
                                {circuit.tokens.map((token) => {
                                  return (
                                    <Grid container sx={{ mt: 1 }} key={token.id}>
                                      <Grid item xs={12}>
                                        <Grid container>
                                          <Grid item xs={12} sm={8} lg={9}>
                                            <Typography variant="body1">
                                              {t("token")}
                                              {`${
                                                token.tokenType
                                                  ? ` (${token.tokenType}): ${token.code}`
                                                  : `: ${token.code}`
                                              }`}
                                            </Typography>
                                            <Typography variant="body1">
                                              {t("issued")}:{" "}
                                              {moment(token?.created_at).format(
                                                "LL"
                                              )}
                                            </Typography>
                                          </Grid>
                                          <Grid
                                            item
                                            xs={12}
                                            sm={4}
                                            lg={3}
                                            sx={{
                                              textAlign: {
                                                xs: "left",
                                                md: "right"
                                              }
                                            }}
                                          >
                                            {event?.ended &&
                                            token.status.toLowerCase() ===
                                              "active" ? (
                                              <Chip
                                                label={t("submitResults")}
                                                size="small"
                                                sx={{
                                                  width: "100%",
                                                  "&:hover": {
                                                    cursor: "pointer"
                                                  }
                                                }}
                                                color="primary"
                                                onClick={async () => {
                                                  try {
                                                    let body = {
                                                      leagueId: circuit.id,
                                                      tokenCode: token.code
                                                    };
                                                    setMessage(
                                                      t("submittingResults")
                                                    );
                                                    let apiData =
                                                      await postData(
                                                        `/events/${event.id}/submitToLeague`,
                                                        {
                                                          body: body
                                                        }
                                                      );
                                                    if (
                                                      apiData?.message ===
                                                      "success"
                                                    ) {
                                                      let _token = {
                                                        ...token,
                                                        status: "used"
                                                      };
                                                      updateCircuits(_token);
                                                    }
                                                  } catch (err) {
                                                    postNotification(
                                                      "error",
                                                      err
                                                    );
                                                  } finally {
                                                    setMessage(null);
                                                  }
                                                }}
                                              />
                                            ) : (
                                              <Chip
                                                label={token?.status?.toUpperCase()}
                                                size="small"
                                                sx={{ width: "100%" }}
                                                color={
                                                  token.status.toLowerCase() ===
                                                  "active"
                                                    ? "success"
                                                    : token.status.toLowerCase() ===
                                                          "pending" ||
                                                        token.status.toLowerCase() ===
                                                          "used"
                                                      ? "warning"
                                                      : "error"
                                                }
                                              />
                                            )}
                                            {authContext?.authState?.identity?.isAdmin &&
                                              token.status?.toLowerCase() !== "rejected" && (
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  color="error"
                                                  fullWidth
                                                  sx={{ mt: 0.5 }}
                                                  onClick={async () => {
                                                    try {
                                                      await confirm({
                                                        title: t("rejectTokenConfirmTitle"),
                                                        description: t("rejectTokenConfirmDescription"),
                                                        confirmationText: "Ok",
                                                        cancellationText: "Cancel",
                                                        dialogProps: { sx: { zIndex: 99999 } }
                                                      });
                                                    } catch {
                                                      return;
                                                    }
                                                    try {
                                                      setMessage(t("rejectingToken"));
                                                      const updated = await postData(
                                                        `/leaguetokens/${token.id}/updateStatus`,
                                                        { body: { status: "rejected" } }
                                                      );
                                                      updateCircuits({ ...token, ...updated, status: "rejected" });
                                                    } catch (err) {
                                                      postNotification("error", err);
                                                    } finally {
                                                      setMessage(null);
                                                    }
                                                  }}
                                                >
                                                  {t("rejectToken")}
                                                </Button>
                                              )}
                                          </Grid>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  );
                                })}
                              </>
                            )}
                            </Grid>
                            {activeCircuit && (
                              <Grid item xs={12} sx={{ mt: 1 }}>
                            <Tooltip
                              title={
                                eventValues?.testEvent
                                  ? t("testEventCircuitDisabledTooltip")
                                  : ""
                              }
                              arrow
                            >
                              <span>
                                <Button
                                  fullWidth
                                  variant="contained"
                                  color={
                                    circuitsRequested?.includes(circuit?.id)
                                      ? "success"
                                      : "primary"
                                  }
                                  endIcon={
                                    circuitsRequested?.includes(circuit?.id) ? (
                                      <BCPCircleCheck />
                                    ) : null
                                  }
                                  disabled={!activeCircuit || readOnly || eventValues?.testEvent}
                                  onClick={async () => {
                                    var newToken;
                                    var apiData;
                                    if (eventId) {
                                      newToken = {
                                        userId:
                                          authContext?.authState?.identity
                                            ?.userId,
                                        email:
                                          authContext?.authState?.identity
                                            ?.email,
                                        leagueId: circuit.id,
                                        eventId: eventId
                                      };
                                      setMessage(t("requestingToken"));
                                      apiData = await postData(
                                        `/leaguetokens`,
                                        {
                                          body: newToken
                                        }
                                      );
                                      updateCircuits(apiData);
                                      postNotification(
                                        apiData.status === "active"
                                          ? "success"
                                          : apiData.status === "rejected"
                                            ? "error"
                                            : "info",
                                        apiData.status === "active"
                                          ? "Your token has been approved"
                                          : apiData.status === "rejected"
                                            ? "Your token has been rejected"
                                            : "Token requested, awaiting approval"
                                      );
                                      setMessage(null);
                                    } else {
                                      const _circuitsRequested = [
                                        ...circuitsRequested
                                      ];
                                      if (
                                        _circuitsRequested?.includes(
                                          circuit?.id
                                        )
                                      ) {
                                        const index =
                                          _circuitsRequested.indexOf(
                                            circuit?.id
                                          );
                                        if (index > -1) {
                                          _circuitsRequested.splice(index, 1);
                                        }
                                      } else {
                                        _circuitsRequested.push(circuit?.id);
                                      }
                                      setCircuitsRequested(_circuitsRequested);

                                      return;
                                    }
                                  }}
                                >
                                  {circuitsRequested?.includes(circuit?.id)
                                    ? t("selected")
                                    : t("requestToken")}
                                    </Button>
                                  </span>
                                </Tooltip>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      );
                    })
                ) : (
                  <Typography variant="body1">
                    {t("noCircuitsAvailable")}
                  </Typography>
                )}
              </Grid>

              {!eventId && (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    id="create-event-btn"
                  >
                    {t("createEvent")}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>

          {isMdUp && (
            <Grid item md={4} sx={{ display: { xs: "none", md: "block" } }}>
              <Box
                sx={{
                  position: "sticky",
                  top: 145,
                  maxHeight: "calc(100vh - 120px)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center"
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", mb: 1, display: "block" }}
                >
                  {t("eventCardPreview") || "Event Card Preview"}
                </Typography>
                <Box sx={{ flex: "0 1 auto", minHeight: 0 }}>
                  <EventCardPreview
                    event={buildPreviewEvent(values, circuitsRequested)}
                    preview={true}
                  />
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
          </form>
        )}
      </Formik>

      <CircuitMenu circuitMenu={circuitMenu} setCircuitMenu={setCircuitMenu} />
    </Grid>
  );
}

function CircuitMenu(props) {
  const { circuitMenu, setCircuitMenu } = props;
  const { t } = useTranslation("translation", {
    keyPrefix: "components.eventCreation"
  });

  return (
    <Menu
      id="profile-menu"
      open={Boolean(circuitMenu)}
      anchorEl={circuitMenu?.anchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      onClose={() => setCircuitMenu(null)}
      disableAutoFocusItem
      disableScrollLock
    >
      {circuitMenu?.circuit?.id && (
        <MenuItem>
          <Link
            target="_blank"
            href={`/leaderboards/${circuitMenu.circuit.id}`}
            underline="none"
            style={{ textDecoration: "none", color: "inherit" }}
            onClick={() => setCircuitMenu(null)}
          >
            {t("viewCircuitLeaderboards")}
          </Link>
        </MenuItem>
      )}

      {circuitMenu?.circuit?.gameStoreId &&
        circuitMenu?.circuit?.storeLeague && (
          <MenuItem>
            <Link
              target="_blank"
              href={`/organization/${circuitMenu.circuit.gameStoreId}`}
              underline="none"
              style={{ textDecoration: "none", color: "inherit" }}
              onClick={() => setCircuitMenu(null)}
            >
              {t("viewGameStore")}
            </Link>
          </MenuItem>
        )}
    </Menu>
  );
}
